import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { config } from '../config/environment';

/**
 * Public page for QR code reward redemption.
 * No login required — accessed by scanning a QR code from the mobile app.
 * URL: /redeem/:qrCode
 */

interface RewardInfo {
  inventory_id: string;
  server_name: string;
  reward_name: string;
  reward_emoji: string;
  status: string;
  purchased_at: string | null;
}

const RedeemReward: React.FC = () => {
  const { qrCode } = useParams<{ qrCode: string }>();
  const [reward, setReward] = useState<RewardInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemed, setRedeemed] = useState(false);

  const apiBase = `${config.restaurantBackendHost.startsWith('localhost') ? 'http' : 'https'}://${config.restaurantBackendHost}`;

  useEffect(() => {
    if (!qrCode) {
      setError('No QR code provided');
      setLoading(false);
      return;
    }

    fetch(`${apiBase}/api/mobile/inventory/qr/${qrCode}`)
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || 'QR code not found');
        }
        return res.json();
      })
      .then((data) => {
        setReward(data);
        if (data.status === 'used') {
          setRedeemed(true);
        }
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Failed to load reward');
        setLoading(false);
      });
  }, [qrCode, apiBase]);

  const handleRedeem = async () => {
    if (!reward) return;
    setRedeeming(true);

    try {
      const res = await fetch(`${apiBase}/api/mobile/inventory/${reward.inventory_id}/use?used_by=qr_scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to redeem');
      }

      setRedeemed(true);
    } catch (err: any) {
      setError(err.message || 'Failed to redeem');
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#FFFFFF',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      {loading && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>🎁</div>
          <div style={{ fontSize: 16, color: '#AFAFAF', fontWeight: 600 }}>Loading reward...</div>
        </div>
      )}

      {error && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>😕</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#4B4B4B', marginBottom: 8 }}>Oops!</div>
          <div style={{ fontSize: 14, color: '#AFAFAF' }}>{error}</div>
        </div>
      )}

      {!loading && !error && reward && !redeemed && (
        <div style={{ textAlign: 'center', maxWidth: 360 }}>
          <div style={{ fontSize: 64, marginBottom: 8 }}>{reward.reward_emoji}</div>
          <div style={{ fontSize: 24, fontWeight: 900, color: '#131F24', marginBottom: 4 }}>{reward.reward_name}</div>
          <div style={{ fontSize: 14, color: '#AFAFAF', marginBottom: 4 }}>For: {reward.server_name}</div>
          <div style={{
            display: 'inline-block',
            background: '#E8F5E9',
            borderRadius: 12,
            padding: '6px 16px',
            marginBottom: 24,
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#58CC02' }}>✓ Available</span>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{
              fontSize: 12,
              color: '#AFAFAF',
              marginBottom: 4,
            }}>
              QR Code: {qrCode}
            </div>
          </div>

          <button
            onClick={handleRedeem}
            disabled={redeeming}
            style={{
              width: '100%',
              padding: '16px 24px',
              background: redeeming ? '#AFAFAF' : '#58CC02',
              border: 'none',
              borderBottom: `4px solid ${redeeming ? '#999' : '#46A302'}`,
              borderRadius: 16,
              color: '#FFFFFF',
              fontSize: 17,
              fontWeight: 800,
              cursor: redeeming ? 'not-allowed' : 'pointer',
              textTransform: 'uppercase',
              letterSpacing: 0.8,
            }}
          >
            {redeeming ? 'PROCESSING...' : 'MARK AS REDEEMED'}
          </button>
          <div style={{ fontSize: 12, color: '#AFAFAF', marginTop: 8 }}>
            Tap to confirm this reward has been used
          </div>
        </div>
      )}

      {!loading && !error && redeemed && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 64, marginBottom: 8 }}>🎉</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: '#58CC02', marginBottom: 8 }}>Redeemed!</div>
          <div style={{ fontSize: 48, marginBottom: 8 }}>{reward?.reward_emoji}</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#131F24', marginBottom: 4 }}>{reward?.reward_name}</div>
          <div style={{ fontSize: 14, color: '#AFAFAF' }}>For: {reward?.server_name}</div>
          <div style={{
            marginTop: 24,
            display: 'inline-block',
            background: '#F7F7F7',
            borderRadius: 14,
            padding: '12px 24px',
            border: '2px solid #E5E5E5',
          }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#AFAFAF' }}>✓ This reward has been used</span>
          </div>
        </div>
      )}

      {/* Bespoke branding */}
      <div style={{ marginTop: 40, fontSize: 12, color: '#E5E5E5', fontWeight: 700 }}>
        Powered by Bespoke
      </div>
    </div>
  );
};

export default RedeemReward;
